"use client";

import { Button } from "@/components/atoms/buttons";
import { toast, Toast } from "@/components/atoms/toast";
import ConfirmDelete from "@/components/molecules/confirm-delete";
import ModalTemplate from "@/components/molecules/modal-template";
import { DialogClose } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useCustomer } from "@/hooks/useCustomer";
import { Locales } from "@/lib/i18n";
import { __ } from "@/lib/i18n/functions";
import { sdk } from "@/lib/medusa/client";
import { getQueryClient } from "@/lib/query";
import { companyEmployeesQueryKey, useAllEmployees, useCompanyDetails } from "@/queries/company";
import { Employee } from "@/types";
import { Loader2 } from "lucide-react";
import { useState, useTransition } from "react";

export type AccountEmployeeDetailsProps = {
  locale: Locales;
};

const AccountEmployeeDetails = ({ locale }: AccountEmployeeDetailsProps) => {
  const queryClient = getQueryClient();
  const [isPending, startTransition] = useTransition();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [tempAdminStatus, setTempAdminStatus] = useState<boolean>(false);
  const { customer } = useCustomer();

  const companyName = customer?.company_name;
  const { data: companyDetails } = useCompanyDetails(companyName || "", customer?.id || "anonymous");
  const { data: employees } = useAllEmployees(companyDetails?.id || "");

  const currentEmployee = employees
    ?.filter((employee) => employee.customer)
    .find((filteredEmployee) => filteredEmployee.customer.id === customer?.id);

  const handleEditEmployee = (employee: Employee) => {
    setEditingEmployee(employee);
    setTempAdminStatus(employee.is_admin);
  };

  const handleUpdateEmployeeAdmin = () => {
    if (!editingEmployee?.id || !companyDetails?.id) return;

    startTransition(async () => {
      try {
        await sdk.client.fetch(`store/companies/${companyDetails.id}/employees/${editingEmployee.id}`, {
          method: "POST",
          body: {
            is_admin: tempAdminStatus,
          },
        });

        queryClient.invalidateQueries({ queryKey: companyEmployeesQueryKey(companyDetails.id!) });
        toast.success({ title: __("Employee updated successfully!", locale) });
        setEditingEmployee(null);
      } catch (error) {
        console.error("Error updating employee:", error);
        toast.error({ title: __("Something went wrong. Please try again later.", locale) });
      }
    });
  };

  const handleEmployeeDelete = (employeeId: string) => {
    if (!employeeId || !companyDetails?.id) return;

    startTransition(async () => {
      try {
        await sdk.client.fetch(`store/companies/${companyDetails?.id}/employees/${employeeId}`, {
          method: "DELETE",
        });
        queryClient.invalidateQueries({ queryKey: companyEmployeesQueryKey(companyDetails.id!) });
        toast.success({ title: __("Employee deleted successfully!", locale) });
        setIsModalOpen(false);
      } catch (error) {
        console.error("Error deleting employees:", error);
        toast.error({ title: __("Something went wrong. Please try again later.", locale) });
      }
    });
  };

  return (
    <section className="border-primary-light/50 space-y-5 border-t pt-5">
      <h2 className="font-cabinet-grotesk text-2xl font-medium md:text-3xl">{__("Collaborators", locale)}</h2>

      {companyName ? (
        <ul className="space-y-5 [&_li:last-child]:border-0 [&_li:last-child]:pb-0">
          {employees &&
            employees.length >= 1 &&
            employees.map((employee) => {
              if (!employee?.customer?.id) return null;

              return (
                <li
                  key={employee.id}
                  className="border-primary-light/50 flex items-start justify-between gap-2.5 border-b pb-4.5 max-sm:flex-col"
                >
                  <div className="space-y-1.5">
                    <dl className="flex items-center gap-1.5">
                      <dt className="flex items-center gap-1.5 font-medium">
                        {employee.customer.first_name} {employee.customer.last_name}{" "}
                        {employee.customer.id === customer.id && `( ${__("You", locale)} )`}
                        {employee.is_admin && <hr className="border-primary-light/50 w-5" />}
                      </dt>
                      {companyDetails?.created_by === employee.customer.id && (
                        <dd className="font-semibold">{__("Owner", locale)}</dd>
                      )}
                      {employee.is_admin && companyDetails?.created_by !== employee.customer.id && (
                        <dd className="font-semibold">Admin</dd>
                      )}
                    </dl>
                    <dl className="flex items-center gap-1.5">
                      <dt className="sr-only">{__("Email", locale)}</dt>
                      <dd className="text-primary-light">{employee.customer.email}</dd>
                    </dl>
                  </div>
                  {(() => {
                    const isMe = employee.customer.id === customer?.id;
                    const isEmployeeOwner = employee.customer.id === companyDetails?.created_by;
                    const isViewerOwner = customer?.id === companyDetails?.created_by;
                    const isViewerAdmin = currentEmployee?.is_admin;

                    if (isMe) return false; // Never show controls for self
                    if (isViewerOwner) return true; // Owner can control everyone else
                    if (isViewerAdmin && !isEmployeeOwner) return true; // Admin can control everyone except owner

                    return false;
                  })() && (
                    <div className="flex items-center gap-3 max-sm:w-full">
                      <ModalTemplate
                        title={__("Edit Employee Permissions", locale)}
                        triggerAsChild
                        open={editingEmployee?.id === employee.id}
                        onOpenChange={(open) => {
                          if (!open) {
                            setEditingEmployee(null);
                            setTempAdminStatus(false);
                          }
                        }}
                        modalTrigger={
                          <Button
                            variant="secondary"
                            className="h-11 flex-1"
                            size="md"
                            onClick={() => handleEditEmployee(employee)}
                          >
                            {__("Edit", locale)}
                          </Button>
                        }
                      >
                        <div className="mx-5 space-y-1.5 border-t py-5">
                          <dl className="flex items-center gap-1.5">
                            <dt className="flex items-center gap-1.5 font-medium">
                              {employee.customer.first_name} {employee.customer.last_name}
                              {employee.is_admin && <hr className="border-primary-light/50 w-5" />}
                            </dt>
                            {companyDetails?.created_by === employee.customer.id && (
                              <dd className="font-semibold">Owner</dd>
                            )}
                            {employee.is_admin && companyDetails?.created_by !== employee.customer.id && (
                              <dd className="font-semibold">Admin</dd>
                            )}
                          </dl>
                          <dl className="flex items-center gap-1.5">
                            <dt className="sr-only">{__("Email", locale)}</dt>
                            <dd className="text-primary-light">{employee.customer.email}</dd>
                          </dl>

                          <Switch
                            id={`${employee.id}-${employee.customer.id}`}
                            label={__("Admin Privileges", locale)}
                            description={__(
                              "Grant this employee admin access to manage company settings and other employees & invite other employees.",
                              locale
                            )}
                            checked={tempAdminStatus}
                            onCheckedChange={setTempAdminStatus}
                            className="mt-4"
                          />

                          <div className="mt-4 flex items-center justify-end gap-2.5">
                            <DialogClose asChild>
                              <Button variant="secondary" className="h-11" size="md">
                                {__("Cancel", locale)}
                              </Button>
                            </DialogClose>
                            <Button
                              size="md"
                              className="h-11"
                              variant="rounded"
                              onClick={handleUpdateEmployeeAdmin}
                              disabled={isPending || companyDetails?.created_by === employee.customer.id}
                            >
                              {isPending && <Loader2 className="mr-2 animate-spin" />} {__("Save", locale)}
                            </Button>
                          </div>
                        </div>
                      </ModalTemplate>
                      <ConfirmDelete
                        onClick={() => handleEmployeeDelete(employee.id)}
                        isActionHappening={isPending}
                        open={isModalOpen}
                        onOpenChange={setIsModalOpen}
                        title={__("Delete employee", locale)}
                        text={__("Are you sure you want to delete this employee?", locale)}
                        locale={locale}
                        modalTrigger={
                          <Button
                            onClick={() => setIsModalOpen(true)}
                            variant="secondary"
                            className="h-11 flex-1"
                            size="md"
                          >
                            {__("Delete", locale)}
                          </Button>
                        }
                      />
                    </div>
                  )}
                </li>
              );
            })}
        </ul>
      ) : (
        <Toast
          id="em-collab"
          type="info"
          title={__("Invite employees to add collaborators.", locale)}
          className="max-w-none shadow-none"
        />
      )}
    </section>
  );
};

export default AccountEmployeeDetails;
